/**
 * core.ts
 * ---------------------------------------------------------------------------
 * ЕДИНЫЙ источник правды для config → schema.org entity (бывш.
 * ep-research/assets/js/components-core.js, теперь TS).
 *
 * Используется ДВУМЯ потребителями:
 *   • Тема (browser): components/browser.ts строит inline JSON-LD каждого
 *     компонента через buildEntity() (через render.ts ldFor).
 *   • Origin (Node-прокси): transform/build-graph.ts собирает @graph.
 *
 * Контракт builder'а:  fn(config, ctx) → entity | null
 *   null   — у компонента нет своей schema.org сущности (BLUF — часть Article,
 *            Radar Chart — визуальный артефакт, FAQ — видимый блок без FAQPage
 *            по ревизии 02.06).
 *   entity — узел для @graph / inline LD, БЕЗ @context (его добавляет caller).
 *
 * Каждый builder валидируется против схемы schema.org через `satisfies <Type>`
 * (schema-dts). Условные поля собираются спредом, чтобы порядок ключей (а значит
 * и JSON-вывод) совпадал с прежней реализацией. Поля @id / isPartOf / creator
 * появляются только когда соответствующий ctx есть.
 * ---------------------------------------------------------------------------
 */

import type {
    CreativeWork, Dataset, SoftwareApplication,
} from 'schema-dts';
import type { LdNode } from '../schema';
import type {
    BuilderContext, ComponentType,
    MethodologyConfig, BenchmarkTableConfig, ModelCardConfig,
    DatasetDownloadConfig,
} from './types';

type Builder = (config: any, ctx: BuilderContext) => LdNode;

const BUILDERS: Partial<Record<ComponentType, Builder>> = {
    'methodology-box': methodologyBox,
    'benchmark-table': benchmarkTable,
    'model-card':      modelCard,
    'prompt-pack':     promptPack,
    'data-download':   dataDownload,
    // 'bluf', 'radar-chart', 'faq', 'changelog', 'finding' — нет сущности. BLUF —
    // часть Article (текст → Article.abstract); radar — визуальный артефакт; FAQ —
    // ревизия 02.06 (видимый блок без FAQPage); changelog — нет валидного типа
    // schema.org (reviewedBy/Article.changelog не в словаре), только видимый текст.
    // finding — то же самое: schema.org `Claim` под наш кейс не подходит (он про
    // разбор спорных утверждений с appearance/firstAppearance), а ценность блока
    // и так в видимом тексте под якорем #finding-N.
};

export function buildEntity(
    type: string,
    config: Record<string, any>,
    ctx: BuilderContext = {},
): LdNode | null {
    const fn = BUILDERS[type as ComponentType];
    if (!fn || !config) return null;

    // Static-вариант — config содержит распарсенный LD от рендера в браузере.
    // Используем его как есть, но переписываем @id под наш контекст.
    if (config.__fromLd) {
        const ent: LdNode = { ...config.__fromLd, '@id': ctx.anchor };
        delete ent['@context']; // в @graph один общий @context на корне
        return ent;
    }

    try {
        return fn(config, ctx);
    } catch (err) {
        // Битый config — не валим всю сборку, скипаем entity.
        console.error('[components-core] build failed', type, err instanceof Error ? err.message : err);
        return null;
    }
}

// ── 2. Methodology Box ───────────────────────────────────────────────────
function methodologyBox(c: MethodologyConfig, ctx: BuilderContext): LdNode {
    const desc = 'Prompts: ' + (c.prompts_count || 0)
        + (c.models ? '. Models: ' + (c.models || []).join(', ') : '');
    return {
        '@type': 'CreativeWork',
        'additionalType': 'Methodology',
        'name': c.title || 'Benchmark methodology',
        'description': desc,
        ...(ctx.anchor ? { '@id': ctx.anchor } : {}),
        ...(ctx.articleId ? { isPartOf: { '@id': ctx.articleId } } : {}),
        ...(c.dates ? { dateCreated: c.dates } : {}),
        ...(c.reviewer ? { creator: { '@type': 'Person', name: c.reviewer } } : {}),
    } satisfies CreativeWork;
}

// ── 3. Benchmark Table ───────────────────────────────────────────────────
function benchmarkTable(c: BenchmarkTableConfig, ctx: BuilderContext): LdNode {
    const columns = c.columns || [];
    return {
        '@type': 'Dataset',
        'name': c.caption || 'Benchmark Results',
        ...(ctx.anchor ? { '@id': ctx.anchor } : {}),
        ...(ctx.articleId ? { isPartOf: { '@id': ctx.articleId } } : {}),
        ...(ctx.authorId ? { creator: { '@id': ctx.authorId } } : {}),
        ...(columns.length > 1 ? { variableMeasured: columns.slice(1) } : {}),
        ...(c.unit ? { measurementTechnique: c.unit } : {}),
        ...(c.source_link ? {
            distribution: [{
                '@type': 'DataDownload',
                'contentUrl': c.source_link,
                'encodingFormat': inferFormat(c.source_link, 'CSV'),
            }],
        } : {}),
    } satisfies Dataset;
}

// ── 5. Model Card ─────────────────────────────────────────────────────────
function modelCard(c: ModelCardConfig, ctx: BuilderContext): LdNode {
    return {
        '@type': 'SoftwareApplication',
        '@id': modelCardId(c, ctx),
        'name': c.name,
        'applicationCategory': c.category || 'AIApplication',
        'operatingSystem': 'Web API',
        ...(c.provider ? { author: { '@type': 'Organization', name: c.provider } } : {}),
        ...(c.license ? { license: c.license } : {}),
        ...(c.cost ? { offers: { '@type': 'Offer', price: normalizePrice(c.cost), priceCurrency: 'USD' } } : {}),
        // hit_rate НЕ инжектим как aggregateRating: Google self-serving review policy
        // (рейтинг от контент-провайдера без независимых отзывов). Значение живёт в
        // видимом тексте карточки/Benchmark Table — его и цитирует ИИ (RES-20).
        ...(c.url ? { url: absoluteUrl(c.url, ctx) } : {}),
    } satisfies SoftwareApplication;
}

// ── 6. Prompt Pack Download ─────────────────────────────────────────────
function promptPack(c: DatasetDownloadConfig, ctx: BuilderContext): LdNode {
    return datasetWithDownload(c, ctx, {
        defaultName: 'Prompt Pack',
        keywords: ['prompts', 'benchmark inputs'],
    });
}

// ── 7. FAQ Block — без schema.org сущности (ревизия 02.06, см. BUILDERS) ────

// ── 8. Raw Data Download ───────────────────────────────────────────────────
function dataDownload(c: DatasetDownloadConfig, ctx: BuilderContext): LdNode {
    return datasetWithDownload(c, ctx, {
        defaultName: 'Experiment data',
        keywords: ['benchmark', 'experiment results'],
    });
}

// ── helpers ─────────────────────────────────────────────────────────────

interface DatasetDownloadOpts {
    defaultName: string;
    keywords: string[];
}

function datasetWithDownload(c: DatasetDownloadConfig, ctx: BuilderContext, opts: DatasetDownloadOpts): LdNode {
    return {
        '@type': 'Dataset',
        'name': c.title || opts.defaultName,
        'keywords': opts.keywords,
        ...(ctx.anchor ? { '@id': ctx.anchor } : {}),
        ...(ctx.articleId ? { isPartOf: { '@id': ctx.articleId } } : {}),
        ...(ctx.authorId ? { creator: { '@id': ctx.authorId } } : {}),
        ...(c.license ? { license: normalizeLicense(c.license) } : {}),
        ...(c.schema_link ? { url: c.schema_link } : {}),
        ...(c.file_url ? {
            distribution: [{
                '@type': 'DataDownload',
                'contentUrl': c.file_url,
                'encodingFormat': inferFormat(c.file_url, c.format || 'CSV'),
            }],
        } : {}),
    } satisfies Dataset;
}

function modelCardId(c: ModelCardConfig, ctx: BuilderContext): string {
    // Канонический @id для модели — общий между всеми статьями. Берём slug из url
    // если есть, иначе из name. Это обеспечивает дедуп Model Card в @graph.
    if (c.url) return absoluteUrl(c.url, ctx) + '#model';
    const slug = String(c.name || 'unknown')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    return (ctx.siteUrl || '') + '/model-cards/' + slug + '#model';
}

function absoluteUrl(url: string, ctx: BuilderContext): string {
    if (/^https?:\/\//.test(url)) return url;
    if (url.charAt(0) === '/') return String(ctx.siteUrl || '').replace(/\/$/, '') + url;
    return new URL(url, ctx.pageUrl).toString();
}

function inferFormat(url: string, fallback: string): string {
    const m = /\.([a-z0-9]+)(?:\?|#|$)/i.exec(url);
    if (!m || !m[1]) return fallback;
    const ext = m[1].toLowerCase();
    const map: Record<string, string> = {
        csv: 'text/csv',
        json: 'application/json',
        jsonl: 'application/jsonl',
        parquet: 'application/vnd.apache.parquet',
        pdf: 'application/pdf',
        zip: 'application/zip',
    };
    return map[ext] || fallback;
}

function normalizeLicense(license: string): string {
    // Google требует URL для license, не строку.
    const map: Record<string, string> = {
        'cc-by 4.0':    'https://creativecommons.org/licenses/by/4.0/',
        'cc by 4.0':    'https://creativecommons.org/licenses/by/4.0/',
        'cc-by-sa 4.0': 'https://creativecommons.org/licenses/by-sa/4.0/',
        'cc0 1.0':      'https://creativecommons.org/publicdomain/zero/1.0/',
        'cc0':          'https://creativecommons.org/publicdomain/zero/1.0/',
        'mit':          'https://opensource.org/licenses/MIT',
        'apache 2.0':   'https://www.apache.org/licenses/LICENSE-2.0',
    };
    const key = String(license).trim().toLowerCase();
    if (map[key]) return map[key]!;
    if (/^https?:\/\//.test(license)) return license;
    return license; // оставляем как есть, Google warning'нёт но не сломается
}

function normalizePrice(cost: string): string {
    // "$0.005 / image" → "0.005"
    const m = /([\d.]+)/.exec(String(cost));
    return m && m[1] ? m[1] : String(cost);
}
